<script lang="ts" setup>
import { AppTableRequestPayload } from '~/composables/api/_types';
import { getOrdersOverviewColumnConfig } from '~/configs/orders';
import { FilterOperator, OrderState } from '~/enums/_types';
import { AppUserType } from '~/enums/permissions/_types';

const { loggedUser } = useAuthUser();
const { isLoading, data, pagination, request } = useOrders({ rowsPerPage: 20, sortBy: 'CreatedAt', descending: true }, [
    { column: 'Status', op: FilterOperator.Neq, val: OrderState.Concept },
]);

const columns = getOrdersOverviewColumnConfig(loggedUser.value?.Type === AppUserType.Partner ? ['Partner'] : undefined);

const onRequest = async (requestPayload: AppTableRequestPayload) => {
    await request(requestPayload);
};

onMounted(() => {
    request();
});
</script>
<template>
    <div v-if="data && data.length > 0">
        <h2 class="q-mt-xl a-text-26 text-bold">{{ $t('orders.overview.last-orders') }}</h2>
        <app-table
            v-model:pagination="pagination"
            sticky-column="last"
            :loading="isLoading"
            :columns="columns"
            :rows="data"
            row-key="Id"
            @request="onRequest"
        >
            <template #body-cell-ReferenceNumber="scope">
                <q-td name="ReferenceNumber" :props="scope">
                    <app-cell-ellipsis :text="scope.row.ReferenceNumber" />
                </q-td>
            </template>
            <template #body-cell-CreatedAt="scope">
                <q-td name="CreatedAt" :props="scope">
                    {{ scope.row.CreatedAt.toLocaleString() }}
                </q-td>
            </template>
            <template #body-cell-RequiredOrShipAt="scope">
                <q-td name="RequiredOrShipAt" :props="scope">
                    {{ scope.row.RequiredOrShipAt?.toLocaleString() }}
                </q-td>
            </template>
            <template #body-cell-Status="scope">
                <q-td
                    name="Status"
                    :props="scope"
                    class="text-bold"
                    :class="`enum-text-orderState--${scope.row.Status}`"
                >
                    {{ $t(`enums.order_statuses.${scope.row.Status}`) }}
                </q-td>
            </template>
            <template #body-cell-Actions="scope">
                <q-td key="Actions" :props="scope">
                    <flat-btn
                        dense
                        icon="las la-file-alt"
                        :to="{
                            name: scope.row.Status === OrderState.Concept ? 'orders-id-new' : 'orders-id',
                            params: { id: scope.row.Id },
                        }"
                    />
                </q-td>
            </template>
        </app-table>
    </div>
    <div v-else>
        <h2 class="q-mt-xl q-mb-sm a-text-26 text-bold">
            {{ $t('orders.overview.noData.noOrders') }}
        </h2>
        <div class="q-mb-md a-text-20 text-on-background-1">
            {{ $t('orders.overview.noData.wantToAdd') }}
            <link-btn
                class="q-pa-none min-height-none"
                style="font-size: 1.25rem"
                text-color="on-background-1 vertical-baseline"
                :label="$t('orders.overview.noData.link')"
                :to="{ name: 'orders-new' }"
            />
        </div>
    </div>
</template>
